import type { ReactNode } from 'react';
import { blockRunsOn } from '@kioskforge/layout';
import type { Block, Capabilities, Layout } from '@kioskforge/shared';
import { DEVICE_PANELS, previewScreen, screenRects, type DeviceVariant } from './grid';
import { ClockWidgetView } from './clockWidget';
import { WeatherWidgetView } from './weatherWidget';
import { NewsWidgetView } from './newsWidget';
import { CalendarWidgetView } from './calendarWidget';
import { WIDGET_SAMPLE } from './widgetData';

/**
 * A5b.10 — minimal, static components for each wave-1 block.
 *
 * Deliberately *not* the interactive player blocks (that is f10 P4.x): these
 * render a faithful-enough still so an author can see the arrangement before
 * publishing. No network calls, no media resolution (b10/C2 owns that) — a media
 * block draws its layout slot, not the bytes.
 */

const TEXT_SIZE: Record<'sm' | 'md' | 'lg' | 'xl', number> = { sm: 14, md: 18, lg: 30, xl: 46 };

function MediaBlockView({ block }: { block: Extract<Block, { type: 'media' }> }) {
  const items = block.props.items;
  return (
    <div className="preview-media">
      <span className="preview-tag mono">MEDIA · {block.props.mode} · {block.props.fit}</span>
      {items.length > 0 ? (
        <div className="preview-media-items">
          {items.map((item) => (
            <span key={item.media_id} className="preview-media-item mono">
              [{item.kind === 'video' ? 'V' : 'I'}] {item.media_id.slice(0, 8)} · {item.duration_s}s
            </span>
          ))}
        </div>
      ) : (
        <span className="preview-muted">{block.props.fallback_text ?? 'No media items.'}</span>
      )}
    </div>
  );
}

function TextBlockView({ block }: { block: Extract<Block, { type: 'text' }> }) {
  return (
    <p
      className="preview-text"
      style={{
        fontSize: TEXT_SIZE[block.props.size],
        textAlign: block.props.align,
        fontWeight: block.props.weight === 'bold' ? 700 : 400
      }}
    >
      {block.props.text || <span className="preview-muted">Empty text block.</span>}
    </p>
  );
}

function ButtonsBlockView({ block }: { block: Extract<Block, { type: 'buttons' }> }) {
  const layout = block.props.layout;
  return (
    <div className={`preview-buttons preview-buttons-${layout}`}>
      {block.props.buttons.map((button) => (
        <span key={button.id} className="preview-button">{button.label}</span>
      ))}
    </div>
  );
}

function FormBlockView({ block }: { block: Extract<Block, { type: 'form' }> }) {
  return (
    <div className="preview-form">
      {block.props.fields.map((field) => (
        <label key={field.id} className="preview-field">
          <span className="preview-label">{field.label}{field.required ? ' *' : ''}</span>
          <span className="preview-input" aria-hidden="true">{field.type}</span>
        </label>
      ))}
      <span className="preview-button">{block.props.submit_label}</span>
    </div>
  );
}

function ConsentBlockView({ block }: { block: Extract<Block, { type: 'consent' }> }) {
  return (
    <div className="preview-consent">
      <p className="preview-text">{block.props.text}</p>
      <span className="preview-checkbox"><i aria-hidden="true" />{block.props.checkbox_label}</span>
    </div>
  );
}

function RatingBlockView({ block }: { block: Extract<Block, { type: 'rating' }> }) {
  const scale = Array.from({ length: block.props.scale }, (_, index) => index + 1);
  return (
    <div className="preview-rating">
      <p className="preview-text">{block.props.prompt}</p>
      <div className="preview-scale">
        {scale.map((value) => <span key={value} className="preview-pip mono">{value}</span>)}
      </div>
    </div>
  );
}

function LiveTileBlockView({ block }: { block: Extract<Block, { type: 'live_tile' }> }) {
  return (
    <div className="preview-tile">
      <span className="preview-label">{block.props.label}</span>
      <span className="preview-tile-value mono">
        {block.props.format === 'text' ? 'value' : '00'}
        {block.props.unit ? <small>{block.props.unit}</small> : null}
      </span>
      <span className="preview-tag mono">KEY · {block.props.data_key}</span>
      {block.props.toggle ? <span className="preview-muted">toggle: {block.props.toggle.on.type} / {block.props.toggle.off.type}</span> : null}
    </div>
  );
}

function WebEmbedBlockView({ block }: { block: Extract<Block, { type: 'web_embed' }> }) {
  return (
    <div className="preview-webembed">
      <span className="preview-tag mono">WEB EMBED</span>
      <span className="preview-muted mono">{block.props.url}</span>
      <span className="preview-muted">{block.props.fallback_text}</span>
    </div>
  );
}

function BlockBody({ block }: { block: Block }): ReactNode {
  switch (block.type) {
    case 'media': return <MediaBlockView block={block} />;
    case 'text': return <TextBlockView block={block} />;
    case 'buttons': return <ButtonsBlockView block={block} />;
    case 'form': return <FormBlockView block={block} />;
    case 'consent': return <ConsentBlockView block={block} />;
    case 'rating': return <RatingBlockView block={block} />;
    case 'live_tile': return <LiveTileBlockView block={block} />;
    case 'web_embed': return <WebEmbedBlockView block={block} />;
    case 'clock': return <ClockWidgetView block={block} />;
    // X3.3–X3.5 — the connector-backed widgets render from the frozen sample so
    // the preview is stable and matches A's future golden images.
    case 'weather': return <WeatherWidgetView block={block} binding={WIDGET_SAMPLE} />;
    case 'news': return <NewsWidgetView block={block} binding={WIDGET_SAMPLE} />;
    case 'calendar': return <CalendarWidgetView block={block} binding={WIDGET_SAMPLE} />;
    default: return <span className="preview-muted">Unknown block.</span>;
  }
}

/**
 * One placed block: absolutely positioned in panel pixels at its grid rect, and
 * A5b.12 — greyed with a text reason when the target device class cannot run it
 * (the same `blockRunsOn` result the deployment gate uses).
 */
export function PreviewBlock({
  block,
  rect,
  capabilities,
  screenId
}: {
  block: Block;
  rect: { x: number; y: number; w: number; h: number };
  capabilities: Capabilities;
  screenId: string;
}) {
  const result = blockRunsOn(block, capabilities, screenId);
  return (
    <div
      className={result.ok ? 'preview-block' : 'preview-block preview-block-blocked'}
      data-block-id={block.id}
      data-block-type={block.type}
      data-block-available={result.ok ? 'true' : 'false'}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
    >
      <BlockBody block={block} />
      {!result.ok ? (
        <span className="preview-blocked-tag mono" title={result.reasons.join(' ')}>
          NOT ON {capabilities.device_class.toUpperCase()}
        </span>
      ) : null}
    </div>
  );
}

/** Every block of one screen, placed at a variant's panel pixels. */
export function PreviewScreen({
  layout,
  screenId,
  variant,
  capabilities
}: {
  layout: Layout;
  screenId: string;
  variant: DeviceVariant;
  capabilities: Capabilities;
}) {
  return (
    <>
      {screenRects(layout, screenId, variant).map(({ block, rect }) => (
        <PreviewBlock key={block.id} block={block} rect={rect} capabilities={capabilities} screenId={screenId} />
      ))}
    </>
  );
}

/**
 * The full preview canvas: the chosen screen's blocks positioned absolutely on a
 * panel of the variant's real pixel size. The caller (admin `DevicePreview`)
 * scales this fixed-size canvas down to fit; the caption names the true size.
 */
export function DevicePreview({
  layout,
  variant,
  screenId,
  capabilities
}: {
  layout: Layout;
  variant: DeviceVariant;
  screenId?: string;
  capabilities?: Capabilities;
}) {
  const panel = DEVICE_PANELS[variant];
  const screen = previewScreen(layout, screenId);
  const caps = capabilities ?? { device_class: panel.deviceClass, lockdown: 'pwa', touch: true, screen_w: panel.width, screen_h: panel.height, blocks: [], app_version: '0.1.0' };
  return (
    <div
      className="preview-canvas"
      data-preview-variant={variant}
      style={{ width: panel.width, height: panel.height }}
    >
      {screen ? (
        <PreviewScreen layout={layout} screenId={screen.id} variant={variant} capabilities={caps} />
      ) : (
        <span className="preview-muted">No screens.</span>
      )}
    </div>
  );
}
